import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CustomerNav from "@/components/layout/CustomerNav";
import { customerApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { HeartPulse, Sparkles, ClipboardList, Info, Activity } from "lucide-react";
import { MEDICAL_QUESTIONS } from "@/lib/medicalQuestionnaire";
import { MASSAGE_HEALTH_ITEMS } from "@/pages/portal/MassagePreferenceStep";
import { useAuth } from "@/lib/auth";
import { isLongevityUnlocked } from "@/lib/LongevityEngine";

/**
 * My Journey — Guest Portal tab (route: /portal/profile).
 *
 * Iter-Feb2026: the classic Profile page was split into three:
 *   - Identity form (Email/WhatsApp/Password/DOB/Gender) → moved into
 *     the "Edit My Info" popup on the header greeting.
 *   - Loyalty balance card → moved to the persistent header chip.
 *   - This page now surfaces the guest's **clinical journey**:
 *       1. Pre-Treatment Medical Questionnaire summary (with edit link),
 *       2. Massage Preference Card summary (if filled).
 *
 * The engine (ClientPortalEngine.jsx) is untouched — only the consumer
 * page content changed.
 */

const HEALTH_LABEL_BY_KEY = MASSAGE_HEALTH_ITEMS.reduce((acc, it) => {
  acc[it.key] = it.label;
  return acc;
}, {});
const QUESTION_LABEL_BY_KEY = MEDICAL_QUESTIONS.reduce((acc, q) => {
  acc[q.key] = q.label;
  return acc;
}, {});

function fmtBaliDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

export default function CustomerProfile() {
  const [medical, setMedical] = useState(null);
  const [massage, setMassage] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter-367 — Longevity feature-flag gate. The Longevity entry card
  // is only rendered when the signed-in guest's email is on the
  // whitelist. Standard guests never see the link, ensuring the
  // dashboard is discoverable only within the access ring.
  const { customer } = useAuth();
  const longevityUnlocked = isLongevityUnlocked(customer?.email);

  const load = useCallback(async () => {
    setLoading(true);
    const [med, mp] = await Promise.allSettled([
      customerApi.get("/customers/me/medical"),
      customerApi.get("/customers/me/massage-preferences"),
    ]);
    if (med.status === "fulfilled") setMedical(med.value.data || null);
    else toast.error(formatApiError(med.reason));
    if (mp.status === "fulfilled") setMassage(mp.value.data?.preferences || null);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Medical summary — count yes-answered questions and known allergy lines.
  const medicalStats = React.useMemo(() => {
    if (!medical) return null;
    const mq = medical.medical_questionnaire || {};
    const yesKeys = Object.keys(mq).filter((k) => (mq[k]?.answer === "yes"));
    const hasAllergies = !!(medical.medication_allergies || "").trim();
    const cosmeticList = Array.isArray(medical.cosmetic_allergies) ? medical.cosmetic_allergies : [];
    const hasMeds = !!(medical.current_medications || "").trim();
    const answeredCount = Object.keys(mq).filter((k) => mq[k]?.answer).length;
    const filled = hasAllergies || cosmeticList.length > 0 || hasMeds || answeredCount > 0;
    return {
      filled,
      yesLabels: yesKeys.slice(0, 6).map((k) => QUESTION_LABEL_BY_KEY[k] || k),
      totalYes: yesKeys.length,
      medication_allergies: medical.medication_allergies,
      cosmetic_allergies: cosmeticList,
      current_medications: medical.current_medications,
      last_updated: medical.medical_updated_at,
    };
  }, [medical]);

  const massageStats = React.useMemo(() => {
    if (!massage) return null;
    const health = massage.health_conditions || {};
    const yesLabels = Object.keys(health)
      .filter((k) => health[k]?.answer === "yes")
      .map((k) => HEALTH_LABEL_BY_KEY[k] || k);
    return {
      pressure: massage.pressure,
      mood_physically: massage.mood_physically,
      mood_emotionally: massage.mood_emotionally,
      focus_areas: massage.focus_areas || [],
      avoid_areas: massage.avoid_areas || [],
      yesLabels,
      created_at: massage.created_at,
    };
  }, [massage]);

  return (
    <div>
      <CustomerNav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-12">
        <p className="tracking-overline text-[11px] text-muted-foreground">MEMBER</p>
        <h1 className="font-editorial text-4xl sm:text-5xl mt-2 sm:mt-3 mb-2" data-testid="my-journey-heading">
          My Journey
        </h1>
        <p className="text-sm text-muted-foreground mb-6 sm:mb-8 max-w-xl">
          Your health flags and treatment preferences at a glance. Keep these up to date so every visit stays safe and personalised.
        </p>

        {loading && (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading your journey…</p>
        )}

        {!loading && (
          <>
            {/* Medical Questionnaire summary card ---------------------------- */}
            {/* Iter-368 — Minimalist Card Upgrade: the whole tile is one
                click target (wrapped in <Link>). The inner "Edit →" pill
                + ArrowRight glyph were REMOVED per the Clean Tiles rule.
                A soft hover-shadow + border tint gives the guest visual
                feedback that the container itself is the button. */}
            <Link
              to="/portal/medical"
              className="block mb-4 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
              data-testid="journey-medical-link"
            >
              <Card
                className="rounded-2xl cursor-pointer transition-shadow duration-200 hover:shadow-md hover:border-primary/20"
                data-testid="journey-medical-card"
              >
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-full bg-emerald-50 text-emerald-700 flex-shrink-0">
                    <HeartPulse className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div>
                      <p className="text-sm font-medium">Medical questionnaire</p>
                      {medicalStats?.last_updated ? (
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Last updated {fmtBaliDate(medicalStats.last_updated)}
                        </p>
                      ) : (
                        <p className="text-[11px] text-muted-foreground mt-0.5">Not filled yet</p>
                      )}
                    </div>

                    {medicalStats?.filled ? (
                      <div className="mt-3 space-y-2 text-xs" data-testid="journey-medical-summary">
                        {medicalStats.medication_allergies && (
                          <p><b>Medication allergies:</b> {medicalStats.medication_allergies}</p>
                        )}
                        {medicalStats.cosmetic_allergies.length > 0 && (
                          <p><b>Cosmetic allergies:</b> {medicalStats.cosmetic_allergies.join(", ")}</p>
                        )}
                        {medicalStats.current_medications && (
                          <p><b>Current medications:</b> {medicalStats.current_medications}</p>
                        )}
                        {medicalStats.totalYes > 0 && (
                          <p>
                            <b>Health flags:</b>{" "}
                            {medicalStats.yesLabels.join(", ")}
                            {medicalStats.totalYes > medicalStats.yesLabels.length && (
                              <span className="text-muted-foreground"> · +{medicalStats.totalYes - medicalStats.yesLabels.length} more</span>
                            )}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-3">
                        Share allergies, medications and health notes so your therapist can plan safely. Takes about 2 minutes.
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            </Link>

            {/* Massage Preference Card summary — clickable at any time.
                Iter-369: wrapped in <Link> so guests can open + edit
                their preferences anytime (was previously read-only /
                only captured at check-in). The check-in wizard already
                prefills from the newest row on the next massage visit,
                so the guest only tweaks their "mood of the day" at the
                desk. */}
            {massageStats ? (
              <Link
                to="/portal/massage-preferences"
                className="block mb-4 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                data-testid="journey-massage-link"
              >
                <Card
                  className="rounded-2xl cursor-pointer transition-shadow duration-200 hover:shadow-md hover:border-primary/20"
                  data-testid="journey-massage-card"
                >
                <CardContent className="p-5 sm:p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-full bg-teal-50 text-teal-700 flex-shrink-0">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">Massage preferences</p>
                      {massageStats.created_at && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Saved {fmtBaliDate(massageStats.created_at)}
                        </p>
                      )}
                      <div className="mt-3 space-y-2 text-xs" data-testid="journey-massage-summary">
                        {massageStats.pressure && (
                          <p><b>Pressure:</b> <span className="capitalize">{massageStats.pressure}</span></p>
                        )}
                        {(typeof massageStats.mood_physically === "number" || typeof massageStats.mood_emotionally === "number") && (
                          <p>
                            <b>Mood:</b>{" "}
                            {typeof massageStats.mood_physically === "number" && <>Physically {massageStats.mood_physically}/10</>}
                            {typeof massageStats.mood_physically === "number" && typeof massageStats.mood_emotionally === "number" && " · "}
                            {typeof massageStats.mood_emotionally === "number" && <>Emotionally {massageStats.mood_emotionally}/10</>}
                          </p>
                        )}
                        {massageStats.focus_areas.length > 0 && (
                          <p><b>Focus areas:</b> {massageStats.focus_areas.join(", ")}</p>
                        )}
                        {massageStats.avoid_areas.length > 0 && (
                          <p><b>Areas to avoid:</b> {massageStats.avoid_areas.join(", ")}</p>
                        )}
                        {massageStats.yesLabels.length > 0 && (
                          <p><b>Health flags:</b> {massageStats.yesLabels.join(", ")}</p>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-3 flex items-start gap-1">
                        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>Tap this card any time to update your preferences — your therapist reads them before your next massage.</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              </Link>
            ) : (
              <Link
                to="/portal/massage-preferences"
                className="block mb-4 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                data-testid="journey-massage-link"
              >
                <Card
                  className="rounded-2xl cursor-pointer transition-shadow duration-200 hover:shadow-md hover:border-primary/20"
                  data-testid="journey-massage-card-empty"
                >
                <CardContent className="p-5 sm:p-6 flex items-start gap-4">
                  <div className="p-3 rounded-full bg-muted text-muted-foreground flex-shrink-0">
                    <ClipboardList className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Massage preferences</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Set your pressure, mood, focus and areas to avoid ahead of time. Your therapist will see them on your next massage visit.
                    </p>
                  </div>
                </CardContent>
              </Card>
              </Link>
            )}

            {/* Iter-366 — Longevity Dashboard entry point.
                Iter-367 — additionally gated on the email whitelist so
                standard guests never see the link. Backend mirrors the
                gate with a 404 on `/api/longevity/*`.
                Iter-368 — Minimalist Card Upgrade: the whole tile is one
                click target (wrapped in <Link>). Inner "Open →" pill +
                ArrowRight glyph REMOVED per the Clean Tiles rule. */}
            {longevityUnlocked && (
              <Link
                to="/portal/longevity"
                className="block mb-4 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                data-testid="journey-longevity-link"
              >
                <Card
                  className="rounded-2xl cursor-pointer transition-shadow duration-200 hover:shadow-md hover:border-primary/20"
                  data-testid="journey-longevity-card"
                >
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-full bg-primary/10 text-primary flex-shrink-0">
                    <Activity className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div>
                      <p className="text-sm font-medium">Longevity dashboard</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Body composition · Blood markers · VO2 Max · Wellness
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                      Your biometric journey — logged by your Cocoon clinician on each Longevity visit, with side-by-side deltas.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
